// External libraries
// @ts-expect-error untyped-import
import { stripNonDigits } from '@checkout/react-components';
import type { ValidationResult } from '../types';

const invalidSecondaryIdentityNumber = {
  errors: ['secondaryIdentityDocumentNumberInvalid'],
  isValid: false,
};

const validateINNChecksum = (
  value: string,
  factor: number[],
  valueLength: number,
  match: string,
): boolean => {
  let n;
  let sumn = 0;

  for (let i = 0; i < valueLength; i++) {
    sumn = sumn + Number(value[i]) * Number(factor[i]);
  }
  n = sumn % 11;
  n = n === 10 ? 0 : n;

  if (n !== Number(match)) {
    return false;
  }

  return true;
};

const validateINN = (inn: string): ValidationResult => {
  const value = stripNonDigits(inn);
  // Individual INN numbers are 12 digits long.
  if (value.length !== 12) {
    return invalidSecondaryIdentityNumber;
  }
  const factor = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];

  if (!validateINNChecksum(value, factor, value.length - 2, value[10])) {
    return invalidSecondaryIdentityNumber;
  }

  // factor would now be [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]
  factor.unshift(3);

  if (!validateINNChecksum(value, factor, value.length - 1, value[11])) {
    return invalidSecondaryIdentityNumber;
  }

  return { errors: [], isValid: true };
};

const validateSNILSChecksum = (sum: number, checksum: number): boolean => {
  if (sum < 100 && sum === checksum) {
    return true;
  } else if ((sum === 100 || sum === 101) && checksum === 0) {
    return true;
  } else if (sum > 101) {
    const remainder = sum % 101;

    return validateSNILSChecksum(remainder, checksum);
  }

  return false;
};

const validateSNILS = (snils: string): ValidationResult => {
  const value = stripNonDigits(snils);
  // Valid SNILS is 11 digits long and has the correct checksum.
  if (value.length !== 11) {
    return invalidSecondaryIdentityNumber;
  }

  // Checking the check number is carried out only for
  // numbers greater than 1001998.
  if (Number(value) > 1001998) {
    const snilsNumber = value.substring(0, 9);
    const checksum = Number(value.substring(9));
    let sum = 0;
    let multiplier = 1;

    for (let i = snilsNumber.length - 1; i >= 0; i--) {
      sum = sum + Number(snilsNumber.charAt(i)) * multiplier;
      multiplier++;
    }

    if (!validateSNILSChecksum(sum, checksum)) {
      return invalidSecondaryIdentityNumber;
    }
  }

  return { errors: [], isValid: true };
};

const validateFieldHasValue = (value: string): ValidationResult => {
  const isValid = Boolean(value);

  return {
    errors: isValid ? [] : ['valueMissing'],
    isValid,
  };
};

const validationFunctionMap = {
  DEFAULT: validateFieldHasValue,
  PENSION_FUND_ID: validateSNILS,
  TAX_IDENTIFICATION_NUMBER: validateINN,
};

export { validationFunctionMap };
